import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../redux/store';
import {
  fetchSwatchesRequest,
  setSelectedProduct,
  clearCustomizations,
  resetStream,
  customizeRequest,
  regenerateImageRequest,
} from '../../redux/slices/customizationSlice';
import { PRODUCT_CATALOG, type ProductId } from './customizerConfig';
import PageHeader from '../common/PageHeader';
import ProductGrid from './ProductGrid';
import ProductDetailView from './ProductDetailView';

const CustomizerPage: React.FC = () => {
  const dispatch = useDispatch();
  const c = useSelector((s: RootState) => s.customization);
  const product = c.selectedProduct ? PRODUCT_CATALOG[c.selectedProduct as ProductId] : null;

  useEffect(() => {
    dispatch(fetchSwatchesRequest());
  }, [dispatch]);

  // Single generate handler — called only when user clicks the Generate button
  const handleGenerate = useCallback(() => {
    if (!product) return;

    const hasPillowWithFile = !!(c.pillowFile && c.pillowFabricUrl);
    const fabricOnlyPillow = !!(product.pillowConfig && !product.pillowConfig.showImageAndCount && c.pillowFabricUrl);
    const hasPillow = hasPillowWithFile || fabricOnlyPillow;

    const hasAny = c.frameFabricUrl || c.backCushionFabricUrl || c.seatFabricUrl || c.armrestFabricUrl || c.finishUrl || hasPillow || c.nailheadFile;
    if (!hasAny) return;

    (async () => {
      const files: File[] = [];
      for (const img of product.baseImages) {
        try {
          const res = await fetch(img.path);
          const blob = await res.blob();
          files.push(new File([blob], img.filename, { type: blob.type }));
        } catch {
          files.push(new File([], img.filename));
        }
      }

      dispatch(customizeRequest({
        params: {
          accountId: 'ralph_lauren',
          frameFabricUrl: c.frameFabricUrl || undefined,
          backCushionFabricUrl: c.backCushionFabricUrl || undefined,
          seatFabricUrl: c.seatFabricUrl || undefined,
          armrestFabricUrl: c.armrestFabricUrl || undefined,
          finishUrl: c.finishUrl || undefined,
          pillowSize: hasPillow ? (c.pillowSize || 'medium') : undefined,
          pillowCount: hasPillowWithFile ? (c.pillowCount || undefined) : undefined,
          pillowFabricUrl: hasPillow ? (c.pillowFabricUrl || undefined) : undefined,
          nailheadSize: c.nailheadFile ? (c.nailheadSize || undefined) : undefined,
        },
        files: {
          furnitureFiles: files,
          pillowFile: hasPillowWithFile ? c.pillowFile : null,
          nailheadFile: c.nailheadFile,
        },
      }));
    })();
  }, [product, c.frameFabricUrl, c.backCushionFabricUrl, c.seatFabricUrl, c.armrestFabricUrl, c.finishUrl, c.pillowSize, c.pillowCount, c.pillowFabricUrl, c.nailheadSize, c.pillowFile, c.nailheadFile, dispatch]);

  const handleProductSelect = (id: ProductId) => {
    dispatch(setSelectedProduct(id));
    dispatch(clearCustomizations());
    dispatch(resetStream());
  };

  const handleBack = () => {
    dispatch(setSelectedProduct(null));
    dispatch(clearCustomizations());
    dispatch(resetStream());
  };

  const handleRegenerate = useCallback((galleryIndex: number) => {
    const result = c.streamResults[galleryIndex];
    if (result?.cache_hash) {
      dispatch(regenerateImageRequest({
        cacheHash: result.cache_hash,
        imageIndex: galleryIndex,
      }));
    }
  }, [c.streamResults, dispatch]);

  if (!product) {
    return (
      <div className="min-h-screen">
        <PageHeader title="Furniture Visualization" showBackButton={false} />
        <ProductGrid onSelect={handleProductSelect} />
      </div>
    );
  }

  return (
    <div className="h-[90vh] max-h-[90vh] flex flex-col overflow-hidden">
      <PageHeader title={product.name} showBackButton onBackClick={handleBack} />
      <ProductDetailView product={product} onGenerate={handleGenerate} onRegenerate={handleRegenerate} />
    </div>
  );
};

export default CustomizerPage;
